import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Joyride, { Step, CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { TourId, getTourSteps, isValidTourId } from './coachmark-tours';

/**
 * Debug helper - only logs in development environment
 */
const debugLog = (...args: unknown[]) => {
    if (process.env.NODE_ENV === 'development') {
        console.log('[Coachmarks]', ...args);
    }
};

/**
 * Interface for analytics event data
 */
interface AnalyticsEventData {
    tourId?: TourId | null;
    stepIndex?: number;
    timestamp?: string;
    [key: string]: unknown;
}

/**
 * Interface for coachmark context state and actions
 */
interface CoachmarkContextType {
    // State
    isRunning: boolean;
    activeTour: TourId | null;
    stepIndex: number;
    run: boolean;

    // Actions
    startTour: (tourId: TourId) => void;
    stopTour: () => void;
    pauseTour: () => void;
    resumeTour: () => void;
    resetTour: () => void;
    nextStep: () => void;
    previousStep: () => void;

    // Analytics
    onTourEvent: (eventType: string, data?: AnalyticsEventData) => void;
}

/**
 * Default context value - throws error if used outside provider
 */
const defaultContextValue: CoachmarkContextType = {
    isRunning: false,
    activeTour: null,
    stepIndex: 0,
    run: false,
    startTour: () => { throw new Error('startTour must be used within CoachmarkProvider'); },
    stopTour: () => { throw new Error('stopTour must be used within CoachmarkProvider'); },
    pauseTour: () => { throw new Error('pauseTour must be used within CoachmarkProvider'); },
    resumeTour: () => { throw new Error('resumeTour must be used within CoachmarkProvider'); },
    resetTour: () => { throw new Error('resetTour must be used within CoachmarkProvider'); },
    nextStep: () => { throw new Error('nextStep must be used within CoachmarkProvider'); },
    previousStep: () => { throw new Error('previousStep must be used within CoachmarkProvider'); },
    onTourEvent: () => { throw new Error('onTourEvent must be used within CoachmarkProvider'); },
};

const CoachmarkContext = createContext<CoachmarkContextType>(defaultContextValue);

/**
 * Props for the CoachmarkProvider component
 */
interface CoachmarkProviderProps {
    children: ReactNode;
    onAnalyticsEvent?: (eventType: string, data?: AnalyticsEventData) => void;
}

/**
 * CoachmarkProvider - Context provider for managing guided tours
 * 
 * Features:
 * - Centralized tour state management
 * - OMNI design system integration
 * - Analytics event tracking
 * - Accessibility support
 * - Responsive behavior
 */
export const CoachmarkProvider: React.FC<CoachmarkProviderProps> = ({
    children,
    onAnalyticsEvent
}) => {
    // State management
    const [isRunning, setIsRunning] = useState(false);
    const [activeTour, setActiveTour] = useState<TourId | null>(null);
    const [stepIndex, setStepIndex] = useState(0);
    const [run, setRun] = useState(false);

    /**
     * Start a specific tour
     */
    const startTour = useCallback((tourId: TourId) => {
        if (!isValidTourId(tourId)) {
            console.warn(`Invalid tour ID: ${tourId}`);
            return;
        }

        setActiveTour(tourId);
        setStepIndex(0);
        setRun(true);
        setIsRunning(true);

        // Analytics tracking
        onAnalyticsEvent?.('tour_started', { tourId, timestamp: new Date().toISOString() });
    }, [onAnalyticsEvent]);

    /**
     * Stop the current tour
     */
    const stopTour = useCallback(() => {
        const currentTour = activeTour;
        const currentStep = stepIndex;

        setRun(false);
        setIsRunning(false);
        setActiveTour(null);
        setStepIndex(0);

        // Analytics tracking
        if (currentTour) {
            onAnalyticsEvent?.('tour_stopped', {
                tourId: currentTour,
                stepIndex: currentStep,
                timestamp: new Date().toISOString()
            });
        }
    }, [activeTour, stepIndex, onAnalyticsEvent]);

    /**
     * Pause the current tour
     */
    const pauseTour = useCallback(() => {
        setRun(false);
        setIsRunning(false);

        if (activeTour) {
            onAnalyticsEvent?.('tour_paused', {
                tourId: activeTour,
                stepIndex,
                timestamp: new Date().toISOString()
            });
        }
    }, [activeTour, stepIndex, onAnalyticsEvent]);

    /**
     * Resume the current tour
     */
    const resumeTour = useCallback(() => {
        if (activeTour) {
            setRun(true);
            setIsRunning(true);

            onAnalyticsEvent?.('tour_resumed', {
                tourId: activeTour,
                stepIndex,
                timestamp: new Date().toISOString()
            });
        }
    }, [activeTour, stepIndex, onAnalyticsEvent]);

    /**
     * Reset tour to beginning
     */
    const resetTour = useCallback(() => {
        if (activeTour) {
            setStepIndex(0);
            setRun(true);
            setIsRunning(true);

            onAnalyticsEvent?.('tour_reset', {
                tourId: activeTour,
                timestamp: new Date().toISOString()
            });
        }
    }, [activeTour, onAnalyticsEvent]);

    /**
     * Go to next step
     */
    const nextStep = useCallback(() => {
        if (activeTour) {
            const steps = getTourSteps(activeTour);
            const nextIndex = Math.min(stepIndex + 1, steps.length - 1);
            setStepIndex(nextIndex);

            onAnalyticsEvent?.('tour_step_advanced', {
                tourId: activeTour,
                fromStep: stepIndex,
                toStep: nextIndex,
                timestamp: new Date().toISOString()
            });
        }
    }, [activeTour, stepIndex, onAnalyticsEvent]);

    /**
     * Go to previous step
     */
    const previousStep = useCallback(() => {
        if (activeTour) {
            const prevIndex = Math.max(stepIndex - 1, 0);
            setStepIndex(prevIndex);

            onAnalyticsEvent?.('tour_step_back', {
                tourId: activeTour,
                fromStep: stepIndex,
                toStep: prevIndex,
                timestamp: new Date().toISOString()
            });
        }
    }, [activeTour, stepIndex, onAnalyticsEvent]);

    /**
     * Custom analytics event tracking
     */
    const onTourEvent = useCallback((eventType: string, data?: AnalyticsEventData) => {
        onAnalyticsEvent?.(eventType, {
            ...data,
            tourId: activeTour,
            stepIndex,
            timestamp: new Date().toISOString()
        });
    }, [activeTour, stepIndex, onAnalyticsEvent]);

    /**
     * Handle Joyride callback events
     */
    const handleJoyrideCallback = useCallback((data: CallBackProps) => {
        const { status, type, action, index } = data;

        debugLog('Joyride callback:', { status, type, action, index, activeTour });

        // Handle close/skip actions - stop the tour
        if (action === ACTIONS.SKIP || type === EVENTS.TOOLTIP && action === 'close') {
            debugLog('User clicked close/skip, stopping tour');
            stopTour();
            onAnalyticsEvent?.(action === ACTIONS.SKIP ? 'tour_skipped' : 'tour_closed', {
                tourId: activeTour,
                totalSteps: activeTour ? getTourSteps(activeTour).length : 0,
                completedSteps: index + 1,
                timestamp: new Date().toISOString()
            });
            return;
        }

        // Handle tour completion
        if (status === STATUS.FINISHED) {
            debugLog('Tour finished normally');
            stopTour();

            onAnalyticsEvent?.('tour_completed', {
                tourId: activeTour,
                totalSteps: activeTour ? getTourSteps(activeTour).length : 0,
                completedSteps: index + 1,
                timestamp: new Date().toISOString()
            });
            return;
        }

        // Handle tour errors or target not found
        if (status === STATUS.ERROR) {
            console.error('Joyride error:', data);
            onAnalyticsEvent?.('tour_error', {
                tourId: activeTour,
                stepIndex: index,
                error: data,
                timestamp: new Date().toISOString()
            });

            // If target not found, skip to next step or end tour
            if (activeTour) {
                const steps = getTourSteps(activeTour);
                if (index < steps.length - 1) {
                    debugLog('Target not found, advancing to next step');
                    setStepIndex(index + 1);
                } else {
                    debugLog('Tour ended due to target not found');
                    stopTour();
                }
            }
            return;
        }

        // Track individual step interactions - this fires AFTER step completion
        if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
            debugLog('Step completed, advancing from', index, 'to', index + 1);

            onAnalyticsEvent?.('tour_step_completed', {
                tourId: activeTour,
                stepIndex: index,
                action,
                timestamp: new Date().toISOString()
            });

            // Check if this was the last step
            if (activeTour) {
                const steps = getTourSteps(activeTour);
                const nextIndex = index + 1;

                if (nextIndex >= steps.length) {
                    debugLog('Tour complete - manually finishing tour');
                    // This was the last step, manually finish the tour
                    stopTour();
                    onAnalyticsEvent?.('tour_completed', {
                        tourId: activeTour,
                        totalSteps: steps.length,
                        completedSteps: steps.length,
                        timestamp: new Date().toISOString()
                    });
                } else {
                    debugLog('Moving to step:', nextIndex);
                    setStepIndex(nextIndex);
                }
            }
        }

        // Handle previous step
        if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
            debugLog('Going back from', index, 'to', index - 1);

            const prevIndex = Math.max(index - 1, 0);
            setStepIndex(prevIndex);
        }
    }, [activeTour, stopTour, onAnalyticsEvent]);

    // Get current tour steps
    const currentSteps: Step[] = activeTour ? getTourSteps(activeTour) : [];

    // Context value
    const contextValue: CoachmarkContextType = {
        isRunning,
        activeTour,
        stepIndex,
        run,
        startTour,
        stopTour,
        pauseTour,
        resumeTour,
        resetTour,
        nextStep,
        previousStep,
        onTourEvent,
    };

    return (
        <CoachmarkContext.Provider value={contextValue}>
            {children}
            {activeTour && (
                <Joyride
                    steps={currentSteps}
                    run={run}
                    stepIndex={stepIndex}
                    callback={handleJoyrideCallback}
                    continuous={true}
                    showProgress={true}
                    showSkipButton={true}
                    spotlightClicks={true}
                    disableOverlay={false}
                    disableScrolling={false}
                    hideCloseButton={false}
                    scrollToFirstStep={true}
                    debug={false}
                    styles={{
                        options: {
                            primaryColor: '#3182ce',
                            width: 320,
                            zIndex: 10000,
                        },
                        tooltip: {
                            borderRadius: 8,
                            padding: 16,
                            fontSize: 14,
                        },
                        tooltipContainer: {
                            textAlign: 'left',
                        },
                        tooltipTitle: {
                            fontSize: 16,
                            fontWeight: 600,
                            marginBottom: 8,
                            color: '#1a202c',
                        },
                        tooltipContent: {
                            fontSize: 14,
                            lineHeight: 1.5,
                            marginBottom: 16,
                            color: '#2d3748',
                        },
                        tooltipFooter: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: 16,
                            padding: '8px 0',
                        },
                        buttonNext: {
                            backgroundColor: '#3182ce',
                            border: 'none',
                            borderRadius: 6,
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 500,
                            padding: '8px 16px',
                            minWidth: 80,
                            minHeight: 36,
                        },
                        buttonBack: {
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: 6,
                            color: '#4a5568',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 500,
                            padding: '8px 16px',
                            marginRight: 8,
                            minWidth: 80,
                            minHeight: 36,
                        },
                        buttonSkip: {
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#718096',
                            cursor: 'pointer',
                            fontSize: 14,
                            textDecoration: 'underline',
                            padding: '4px 8px',
                        },
                    }}
                    locale={{
                        back: 'Previous',
                        close: 'Close',
                        last: 'Finish Tour',
                        next: 'Next',
                        skip: 'Skip Tour',
                    }}
                    floaterProps={{
                        disableAnimation: false,
                        styles: {
                            floater: {
                                filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.15))',
                            },
                        },
                    }}
                />
            )}
        </CoachmarkContext.Provider>
    );
};

/**
 * Hook to use coachmark context
 * @returns CoachmarkContextType
 * @throws Error if used outside CoachmarkProvider
 */
export const useCoachmarks = (): CoachmarkContextType => {
    const context = useContext(CoachmarkContext);

    if (!context) {
        throw new Error('useCoachmarks must be used within a CoachmarkProvider');
    }

    return context;
};

/**
 * Type exports
 */
export type { CoachmarkContextType, TourId };
